import "./quote-board-grid.css";

import type { SortDescriptor } from "@tql/ui-components-react/kendo/data-query";
import { orderBy, process } from "@tql/ui-components-react/kendo/data-query";
import type {
	GridColumnReorderEvent,
	GridColumnResizeEvent,
	GridCustomCellProps,
	GridCustomRowProps,
	GridPageChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useGridFilterTabNavigation } from "~/shared/hooks/use-grid-filter-tab-navigation.ts";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { distributeVisibleColumnWidths } from "~/shared/utils/grid-column-utils.ts";

import { useExpandIconVisibility } from "../hooks/use-expand-icon-visibility.ts";
import {
	convertToGridColumnProps,
	processGridColumns,
} from "../utils/quote-board-column-processor.ts";
import type { GridCompositeFilterDescriptor } from "../utils/quote-board-grid-filter-utils.ts";
import { isQuoteBoardSelectionEligible } from "../utils/quote-board-status.ts";
import { QuoteBoardGroupHeader } from "./components/quote-board-group-header/quote-board-group-header.tsx";
import { useFilterCssManagement } from "./hooks/use-filter-css-management.ts";
import {
	useGridKey,
	useTransformedGridData,
} from "./hooks/use-grid-data-helpers.ts";
import { useQuoteBoardFilterManagement } from "./hooks/use-quote-board-filter-management.ts";
import { useQuoteBoardGridReset } from "./hooks/use-quote-board-grid-reset.ts";
import { useQuoteBoardGridState } from "./hooks/use-quote-board-grid-state.ts";

export {
	applyFilterClasses,
	forceCleanupFilterClasses,
	scheduleCleanup,
} from "./hooks/use-filter-css-management.ts";

const getCreatedDateTimestamp = (createdDate: string | null): number => {
	if (!createdDate) {
		return Number.NEGATIVE_INFINITY;
	}

	const timestamp = Date.parse(createdDate);
	return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

// Date columns are sorted by full timestamp (date + time) by routing the
// sort through derived numeric fields. Lex-sorting raw values can lose
// precision when the API returns mixed formats (e.g. "2025-09-25" vs
// "2025-09-25T13:43:00Z"), so we normalize to milliseconds for sorting
// while keeping the original string values for display.
const DATE_SORT_FIELD_MAP: Record<string, string> = {
	createdDate: "createdDateSort",
	shipmentDate: "shipmentDateSort",
};

const remapDateSortFields = (sort: SortDescriptor[]): SortDescriptor[] =>
	sort.map((descriptor) => {
		const remapped = DATE_SORT_FIELD_MAP[descriptor.field ?? ""];
		return remapped ? { ...descriptor, field: remapped } : descriptor;
	});

type QuoteBoardGridItem = QuoteBoardItem & {
	displayGroupingId: string | number;
	createdDateSort: number;
	shipmentDateSort: number;
	focusPriority: number;
	tenderPriority: number;
};

type QuoteBoardItemGroup = {
	field: string;
	value: string | number;
	items: QuoteBoardGridItem[];
	groupCreatedDate: string | null;
	groupCreatedDateTimestamp: number;
};

const buildBulkGroupedData = (
	items: QuoteBoardGridItem[],
	sort: ReturnType<typeof useQuoteBoardGridState>["state"]["sort"],
	skip: number,
	take: number,
) => {
	const groupMap = new Map<string | number, QuoteBoardItemGroup>();

	for (const item of items) {
		const key = item.displayGroupingId;
		const groupCreatedDate = item.bulkReceivedAtUtc ?? item.createdDate;
		const itemCreatedDateTimestamp = getCreatedDateTimestamp(groupCreatedDate);

		let group = groupMap.get(key);
		if (!group) {
			group = {
				field: "displayGroupingId",
				value: key,
				items: [],
				groupCreatedDate,
				groupCreatedDateTimestamp: itemCreatedDateTimestamp,
			};
			groupMap.set(key, group);
		}

		if (itemCreatedDateTimestamp < group.groupCreatedDateTimestamp) {
			group.groupCreatedDateTimestamp = itemCreatedDateTimestamp;
			group.groupCreatedDate = groupCreatedDate;
		}

		group.items.push(item);
	}

	const groups = [...groupMap.values()]
		.map((group) => ({
			...group,
			items: orderBy(group.items, sort),
		}))
		.sort((leftGroup, rightGroup) => {
			const focusDifference =
				Number(rightGroup.items.some((item) => item.focusPriority === 1)) -
				Number(leftGroup.items.some((item) => item.focusPriority === 1));
			if (focusDifference !== 0) return focusDifference;

			const priorityDifference =
				Number(rightGroup.items.some((item) => item.tenderPriority === 1)) -
				Number(leftGroup.items.some((item) => item.tenderPriority === 1));
			if (priorityDifference !== 0) return priorityDifference;

			if (
				leftGroup.groupCreatedDateTimestamp !==
				rightGroup.groupCreatedDateTimestamp
			) {
				return (
					rightGroup.groupCreatedDateTimestamp -
					leftGroup.groupCreatedDateTimestamp
				);
			}

			return String(rightGroup.value).localeCompare(String(leftGroup.value));
		});

	return {
		data: groups.slice(skip, skip + take),
		total: groups.length,
	};
};

const buildDefaultGroupedData = (
	items: QuoteBoardGridItem[],
	sort: ReturnType<typeof useQuoteBoardGridState>["state"]["sort"],
	skip: number,
	take: number,
) => {
	const sortedItems = orderBy(items, sort);
	const groups: QuoteBoardItemGroup[] = [];
	const groupMap = new Map<string | number, QuoteBoardItemGroup>();

	for (const item of sortedItems) {
		const key = item.displayGroupingId;
		let group = groupMap.get(key);
		if (!group) {
			group = {
				field: "displayGroupingId",
				value: key,
				items: [],
				groupCreatedDate: item.createdDate,
				groupCreatedDateTimestamp: getCreatedDateTimestamp(item.createdDate),
			};
			groups.push(group);
			groupMap.set(key, group);
		}
		group.items.push(item);
	}

	return {
		data: groups.slice(skip, skip + take),
		total: groups.length,
	};
};

const initialGroup = [
	{
		field: "displayGroupingId",
	},
];

const QuoteBoardRow = (props: GridCustomRowProps) => {
	const quoteBoardItem = props.dataItem as QuoteBoardItem;
	const { trProps, children } = props;
	const isBulk = quoteBoardItem?.bulkQuoteId != null;
	const hasBulkUploadException = quoteBoardItem?.bulkUploadException != null;

	return (
		<tr
			{...trProps}
			className={`${trProps?.className || ""}${isBulk ? " bulk-quote-row" : ""}${hasBulkUploadException ? " bulk-upload-error-row" : ""}`}
			data-is-bulk={isBulk}
			data-testid={"grid-item"}
		>
			{children}
		</tr>
	);
};

type QuoteBoardGridProps = {
	gridData: QuoteBoardItem[];
	tenderedQuoteIds?: readonly number[];
	isLoading: boolean;
	columns: BoardColumn[];
	resetKey?: number;
	hasActiveSearchChips?: boolean;
	quoteType: string;
	isInternal?: boolean;
	/**
	 * Remount key from `useGridPreferencesSync`, bumped on reset / invalid
	 * reorder so Kendo re-reads default column widths and order.
	 */
	prefsGridKey?: number;
	/**
	 * Persists column order (external users only). From `useGridPreferencesSync`.
	 * When omitted (internal users) the grid is not reorderable, matching the
	 * legacy behavior.
	 */
	onColumnReorder?: (event: GridColumnReorderEvent) => void;
	/**
	 * Persists column width (external users only). From `useGridPreferencesSync`.
	 * When omitted (internal users) the grid falls back to legacy in-memory
	 * resizing that is not persisted.
	 */
	onColumnResize?: (event: GridColumnResizeEvent) => void;
	selectedQuoteIds?: ReadonlySet<number>;
	onToggleQuoteSelection?: (quoteId: number, selected: boolean) => void;
	onToggleBatchSelection?: (
		bulkQuoteId: string,
		selected: boolean,
		visibleQuoteIds: readonly number[],
	) => void;
	showBulkTenderSelection?: boolean;
	/** Batch to pin first and reveal expanded, e.g. straight after a bulk upload. */
	focusedBatchId?: string | null;
};

const EMPTY_TENDERED_QUOTE_IDS: readonly number[] = [];

export const QuoteBoardGrid = ({
	gridData,
	tenderedQuoteIds = EMPTY_TENDERED_QUOTE_IDS,
	isLoading,
	columns,
	resetKey = 0,
	hasActiveSearchChips = false,
	quoteType,
	isInternal = false,
	prefsGridKey = 0,
	onColumnReorder,
	onColumnResize,
	selectedQuoteIds = new Set<number>(),
	onToggleQuoteSelection,
	onToggleBatchSelection,
	showBulkTenderSelection = false,
	focusedBatchId,
}: QuoteBoardGridProps) => {
	const { state, actions, handlers, resetInfo, isLoadingPreference } =
		useQuoteBoardGridState(resetKey);
	const revealedFocusedBatchRef = useRef<string | null>(null);

	const dynamicGroup = useMemo(() => {
		if (quoteType === "Individual") {
			return [];
		}
		return initialGroup;
	}, [quoteType]);

	useGridFilterTabNavigation();

	const processedColumns = useMemo(() => {
		const selectionColumn: BoardColumn = {
			field: "bulkTenderSelection",
			title: " ",
			show: true,
			menu: false,
			filterable: false,
			sortable: false,
			reorderable: false,
			fixedWidth: true,
			width: 48,
			cells: {
				data: (props: GridCustomCellProps) => {
					const item = props.dataItem as QuoteBoardItem;
					const isEligible = isQuoteBoardSelectionEligible(item, {
						isInternal,
					});
					return (
						<GridTd tdProps={props.tdProps}>
							<Checkbox
								aria-label={`Select quote ${item.quoteId}`}
								checked={selectedQuoteIds.has(item.quoteId)}
								disabled={!isEligible}
								onChange={(event) =>
									onToggleQuoteSelection?.(item.quoteId, event.value)
								}
							/>
						</GridTd>
					);
				},
			},
		};
		const displayColumns = showBulkTenderSelection
			? [selectionColumn, ...columns]
			: columns;
		return processGridColumns(displayColumns);
	}, [
		columns,
		isInternal,
		onToggleQuoteSelection,
		selectedQuoteIds,
		showBulkTenderSelection,
	]);

	const BulkTenderGroupHeader = useCallback(
		(props: GridCustomCellProps) => (
			<QuoteBoardGroupHeader
				{...props}
				isInternal={isInternal}
				onToggleBatchSelection={onToggleBatchSelection}
				selectedQuoteIds={selectedQuoteIds}
				showBulkTenderSelection={showBulkTenderSelection}
			/>
		),
		[
			isInternal,
			onToggleBatchSelection,
			selectedQuoteIds,
			showBulkTenderSelection,
		],
	);

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [columnWidthOverrides, setColumnWidthOverrides] = useState<
		Map<string, number>
	>(new Map());
	const hasInitializedWidthsRef = useRef(false);

	// Use the first non-empty string of `field` / `title`. `??` alone treats
	// `""` as a real value, so columns that intentionally set `field: ""`
	// (e.g. leading icon columns) would otherwise collide on the same key.
	const getColumnKey = useCallback(
		(col: BoardColumn) =>
			(col.field ? String(col.field) : "") ||
			(col.title ? String(col.title) : "") ||
			"",
		[],
	);

	// External users persist widths to the backend (an `onColumnResize` handler
	// is supplied) and render columns at their canonical/persisted widths, just
	// like the Shipment Board — no viewport auto-distribution. Only internal
	// (legacy) users auto-distribute widths to fill the available space on first
	// mount. Derived from the persistence handler for consistency with the
	// `reorderable={Boolean(onColumnReorder)}` gating below.
	const autoDistributeWidths = !onColumnResize;

	// On initial mount, distribute the visible columns across the wrapper width
	// so the grid fills the available space. After this, column widths are only
	// changed by the user resizing — newly-added columns keep their default
	// widths and the grid is allowed to overflow horizontally.
	//
	// Skipped for external (persisted) users: their widths come from the
	// `columns` prop (canonical or saved), so auto-distribution must not run.
	useEffect(() => {
		if (!autoDistributeWidths) return;
		if (hasInitializedWidthsRef.current) return;
		const wrapper = wrapperRef.current;
		if (!wrapper) return;
		const visibleCount = processedColumns.filter(
			(c) => c.show !== false,
		).length;
		if (visibleCount === 0) return;
		const wrapperWidth = wrapper.clientWidth;
		if (!wrapperWidth) return;

		const scrollbarBuffer = 18;
		const distributed = distributeVisibleColumnWidths(
			processedColumns,
			wrapperWidth - scrollbarBuffer,
		);

		const next = new Map<string, number>();
		for (const col of distributed) {
			if (typeof col.width === "number") {
				next.set(getColumnKey(col), col.width);
			}
		}
		setColumnWidthOverrides(next);
		hasInitializedWidthsRef.current = true;
	}, [processedColumns, getColumnKey, autoDistributeWidths]);

	const distributedColumns = useMemo(() => {
		// External (persisted) users honor the widths carried on the `columns`
		// prop (canonical defaults or their saved prefs) — no local distribution.
		if (!autoDistributeWidths) {
			return processedColumns.map((col) => ({ ...col }));
		}
		return processedColumns.map((col) => {
			const override = columnWidthOverrides.get(getColumnKey(col));
			if (override != null) {
				return { ...col, width: override };
			}
			return { ...col };
		});
	}, [
		processedColumns,
		columnWidthOverrides,
		getColumnKey,
		autoDistributeWidths,
	]);

	// Legacy in-memory resize used when no persistence handler is supplied
	// (internal users). Widths are kept only in component state and reset on
	// remount/refresh, matching the original Quote Board behavior. External
	// users instead receive `onColumnResize` from `useGridPreferencesSync`,
	// which persists widths to the backend.
	const handleLocalColumnResize = useCallback(
		(event: GridColumnResizeEvent) => {
			if (!event.end) return;
			setColumnWidthOverrides((prev) => {
				const next = new Map(prev);
				for (const col of event.columns) {
					const key = String(col.field ?? col.title ?? "");
					if (key && typeof col.width === "number") {
						next.set(key, col.width);
					}
				}
				return next;
			});
		},
		[],
	);

	// Capture initially visible column keys once so subsequent visibility changes
	const initialVisibleKeysRef = useRef<Set<string> | null>(null);
	initialVisibleKeysRef.current ??= new Set(
		processedColumns
			.filter((c) => c.show !== false)
			.map((c) => String(c.field ?? c.title)),
	);

	useQuoteBoardGridReset(
		resetKey,
		resetInfo.shouldForceReset,
		resetInfo.hasProcessedInitialReset,
		resetInfo.lastResetKeyRef,
		actions,
	);

	useEffect(() => {
		actions.setSkip(0);
	}, [quoteType, actions]);

	useEffect(() => {
		revealedFocusedBatchRef.current = null;
		if (focusedBatchId) actions.setSkip(0);
	}, [focusedBatchId, actions]);

	useEffect(() => {
		if (tenderedQuoteIds.length === 0) return;
		actions.setSkip(0);
		actions.setGroupExpand([]);
	}, [tenderedQuoteIds, actions]);

	const activeFilter = resetInfo.shouldForceReset
		? undefined
		: state.controlledFilter;

	const { handleFilterChange } = useQuoteBoardFilterManagement(
		processedColumns,
		activeFilter,
		actions,
		hasActiveSearchChips,
	);

	const handleGridFilterChange = useCallback(
		(event: { filter?: GridCompositeFilterDescriptor | null }) => {
			handleFilterChange(event);
			actions.setSkip(0);
		},
		[handleFilterChange, actions],
	);

	const transformedGridData = useTransformedGridData(
		gridData,
		state.dataRefreshKey,
		isInternal,
	);

	const enrichedData = useMemo(() => {
		const tenderedIds = new Set(tenderedQuoteIds);
		return transformedGridData.map((item) => ({
			...item,
			displayGroupingId: item.bulkQuoteId ?? item.quoteId,
			createdDateSort: getCreatedDateTimestamp(item.createdDate),
			shipmentDateSort: getCreatedDateTimestamp(item.shipmentDate),
			focusPriority:
				focusedBatchId && item.bulkQuoteId === focusedBatchId ? 1 : 0,
			tenderPriority: tenderedIds.has(item.quoteId) ? 1 : 0,
		}));
	}, [transformedGridData, tenderedQuoteIds, focusedBatchId]);

	const filteredData = useMemo(() => {
		if (quoteType === "Bulk") {
			return enrichedData.filter((item) => item.bulkQuoteId !== null);
		}
		if (quoteType === "Individual") {
			return enrichedData.filter((item) => item.bulkQuoteId === null);
		}
		return enrichedData;
	}, [enrichedData, quoteType]);

	const processedData = useMemo(() => {
		const filterState = {
			filter: activeFilter,
		};
		// First apply filtering using standard Kendo process
		const filteredResult = process(filteredData, filterState);
		const items = filteredResult.data as QuoteBoardGridItem[];
		const sortForProcess: SortDescriptor[] = [
			{ field: "focusPriority", dir: "desc" },
			{ field: "tenderPriority", dir: "desc" },
			...remapDateSortFields(state.sort),
		];

		// If we are grouped (by displayGroupingId), we need custom logic to:
		// 1. Sort the items
		// 2. Re-group them manually (preserving sort order of groups based on first item)
		// 3. Handle paging of the GROUPS, not just items
		if (dynamicGroup.length > 0) {
			if (quoteType === "Bulk") {
				return buildBulkGroupedData(
					items,
					sortForProcess,
					state.skip,
					state.take,
				);
			}

			return buildDefaultGroupedData(
				items,
				sortForProcess,
				state.skip,
				state.take,
			);
		}

		// Fallback to standard Kendo processing for non-grouped state
		return process(items, {
			group: dynamicGroup,
			sort: sortForProcess,
			skip: state.skip,
			take: state.take,
		});
	}, [
		filteredData,
		activeFilter,
		dynamicGroup,
		state.sort,
		state.skip,
		state.take,
		quoteType,
	]);

	const dataGridKey = useGridKey(
		filteredData,
		processedColumns,
		state.dataRefreshKey,
		isLoading,
	);

	// Combine the data-driven remount key with the preferences remount key so a
	// reset / invalid-reorder revert (which bumps `prefsGridKey`) forces Kendo
	// to re-read default column widths and order.
	const gridKey = `${dataGridKey}-${prefsGridKey}`;

	useFilterCssManagement(state.filteredColumns, processedColumns);

	useExpandIconVisibility(filteredData);

	// Initialize group expansion to collapsed on first data load
	useEffect(() => {
		if (
			filteredData.length > 0 &&
			state.groupExpand.length === 0 &&
			!isLoading &&
			!resetInfo.shouldForceReset
		) {
			const initialExpand = [
				...new Set(filteredData.map((item) => item.displayGroupingId)),
			].map((id) => {
				const item = filteredData.find((i) => i.displayGroupingId === id);
				const isBulkId = item?.bulkQuoteId != null;
				return {
					field: "displayGroupingId",
					value: id,
					expanded:
						!isBulkId ||
						item?.bulkQuoteId === focusedBatchId ||
						filteredData.some(
							(row) => row.displayGroupingId === id && row.tenderPriority === 1,
						),
				};
			});

			if (initialExpand.length > 0) {
				actions.setGroupExpand(initialExpand);
			}
		}
	}, [
		filteredData,
		actions,
		state.groupExpand.length,
		isLoading,
		resetInfo.shouldForceReset,
		focusedBatchId,
	]);

	useEffect(() => {
		if (
			!focusedBatchId ||
			revealedFocusedBatchRef.current === focusedBatchId ||
			isLoading ||
			resetInfo.shouldForceReset ||
			state.groupExpand.length === 0 ||
			!filteredData.some((item) => item.focusPriority === 1)
		)
			return;

		revealedFocusedBatchRef.current = focusedBatchId;
		actions.setSkip(0);
		if (
			!state.groupExpand.some(
				(group) => group.value === focusedBatchId && group.expanded,
			)
		) {
			actions.setGroupExpand([
				...state.groupExpand.filter((group) => group.value !== focusedBatchId),
				{ field: "displayGroupingId", value: focusedBatchId, expanded: true },
			]);
		}
	}, [
		focusedBatchId,
		filteredData,
		state.groupExpand,
		isLoading,
		resetInfo.shouldForceReset,
		actions,
	]);

	const handlePageChange = (event: GridPageChangeEvent) => {
		handlers.handlePageChange(event, gridData);
	};

	return (
		<div
			ref={wrapperRef}
			aria-busy={isLoading || isLoadingPreference}
			className={"quote-board-grid-wrapper"}
			data-testid={"quote-board-grid"}
		>
			<TraxGrid
				key={gridKey}
				autoProcessData={false}
				cells={{
					group: {
						groupHeader: BulkTenderGroupHeader,
					},
				}}
				className={"quote-board-grid"}
				columns={convertToGridColumnProps(distributedColumns)}
				data={processedData}
				dataItemKey={"quoteId"}
				editable={false}
				filter={activeFilter}
				filterable={true}
				group={dynamicGroup}
				groupExpand={state.groupExpand}
				loading={isLoading || isLoadingPreference}
				loadingRows={10}
				onColumnReorder={onColumnReorder}
				onColumnResize={onColumnResize ?? handleLocalColumnResize}
				onFilterChange={handleGridFilterChange}
				onGroupExpandChange={handlers.handleGroupExpandChange}
				onPageChange={handlePageChange}
				onSortChange={handlers.handleSortChange}
				reorderable={Boolean(onColumnReorder)}
				resizable={true}
				rows={{
					data: QuoteBoardRow,
				}}
				scrollable={"scrollable"}
				skip={state.skip}
				sort={state.sort}
				sortable={{
					allowUnsort: true,
					mode: "single",
				}}
				style={{ height: "100%" }}
				take={state.take}
			/>
		</div>
	);
};
